import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Card } from '@/components/Card';
import { CardHeader } from '@/components/CardHeader';
import { RatioBar } from '@/components/RatioBar';
import { EmptyState } from '@/components/EmptyState';
import { formatNumber, formatRelativeTime } from '@/lib/locale';
import { apiGetPaginated, qs } from '@/features/_shared/queries';
import { QueryState } from '@/features/_shared/QueryState';

type GroupBy = 'ip' | 'asn' | 'esp' | 'country';

type SourceRow = {
  key: string; // group value, e.g. IP / ASN / ESP / country
  display?: string;
  volume: number;
  pass_rate: number;
  reports_count?: number;
  classification?: 'legitimate' | 'misalignment' | 'unknown' | 'suspect';
  last_seen?: string;
};

const TABS: Array<{ id: GroupBy; label: string }> = [
  { id: 'ip', label: 'IP' },
  { id: 'asn', label: 'ASN' },
  { id: 'esp', label: 'ESP' },
  { id: 'country', label: 'Land' },
];

const PER_PAGE = 25;

export function SourcesScreen({ slug }: { slug: string }) {
  const [tab, setTab] = useState<GroupBy>('ip');
  const [page, setPage] = useState(1);

  const params = useMemo(
    () => ({
      tenant: slug,
      group_by: tab,
      page,
      per_page: PER_PAGE,
    }),
    [slug, tab, page],
  );

  const sourcesQ = useQuery({
    queryKey: ['sources', params],
    queryFn: () =>
      apiGetPaginated<SourceRow>(`/analytics/source-groups${qs(params)}`, {
        tenant: slug,
      }),
  });

  const rows = sourcesQ.data?.data ?? [];
  const total = sourcesQ.data?.meta?.total ?? rows.length;
  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  return (
    <div className="max-w-[1400px] space-y-5 p-5">
      <div>
        <h1 className="text-[22px] font-bold tracking-tight text-slate-900">Verzendbronnen</h1>
        <p className="mt-0.5 text-[13px] text-slate-500">
          {formatNumber(total)} unieke bronnen · gegroepeerd per{' '}
          {TABS.find((t) => t.id === tab)?.label.toLowerCase()}
        </p>
      </div>

      {/* Tab strip */}
      <div className="flex items-center gap-1 border-b border-slate-200">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => {
              setTab(t.id);
              setPage(1);
            }}
            className={`-mb-px border-b-2 px-3 py-2 text-[12px] ${
              tab === t.id
                ? 'border-brand-600 font-semibold text-slate-900'
                : 'border-transparent text-slate-500 hover:text-slate-900'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <QueryState
        isLoading={sourcesQ.isLoading}
        isError={sourcesQ.isError}
        onRetry={() => sourcesQ.refetch()}
      >
        {rows.length === 0 ? (
          <Card>
            <EmptyState
              title="Nog geen bronnen waargenomen"
              body="Aggregate rapporten worden vanaf de eerste publicatie van het DMARC-record verwacht."
            />
          </Card>
        ) : (
          <Card>
            <CardHeader title="Bronnen" subtitle={`pagina ${page} van ${totalPages}`} />
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead className="border-b border-slate-200 bg-slate-50">
                  <tr>
                    <Th>{TABS.find((t) => t.id === tab)?.label ?? 'Sleutel'}</Th>
                    <Th align="right">Volume</Th>
                    <Th align="right">Rapporten</Th>
                    <Th>Pass-ratio</Th>
                    <Th>Klassificatie</Th>
                    <Th>Laatst gezien</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((s) => (
                    <tr key={s.key} className="hover:bg-slate-50">
                      <td className="px-3 py-2.5 font-mono text-slate-900">{s.display ?? s.key}</td>
                      <td className="px-3 py-2.5 text-right font-mono tabular-nums">
                        {formatNumber(s.volume)}
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums">
                        {s.reports_count != null ? formatNumber(s.reports_count) : '—'}
                      </td>
                      <td className="px-3 py-2.5">
                        <RatioBar pct={s.pass_rate * 100} />
                      </td>
                      <td className="px-3 py-2.5 text-slate-700">{s.classification ?? '—'}</td>
                      <td className="px-3 py-2.5 text-[11px] text-slate-500">
                        {s.last_seen ? formatRelativeTime(s.last_seen) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-slate-100 px-4 py-3 text-[11px]">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="rounded border border-slate-200 px-2 py-1 hover:bg-slate-50 disabled:opacity-30"
              >
                Vorige
              </button>
              <span className="text-slate-500">
                {page} / {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="rounded border border-slate-200 px-2 py-1 hover:bg-slate-50 disabled:opacity-30"
              >
                Volgende
              </button>
            </div>
          </Card>
        )}
      </QueryState>
    </div>
  );
}

function Th({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return (
    <th
      className={`px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500 text-${align}`}
    >
      {children}
    </th>
  );
}
